/**
 * Presenting Problems defaults, pre-fill from the referral, and the rules
 * checked before signing (mirrors validate_forms.go on the server).
 */
import type { PrpPPGoal, PrpPresentingProblemsContent, PrpReferralContent } from "@/api/prp.api";
import { parseOnto } from "./use-draft";

export function emptyGoal(): PrpPPGoal {
  return { goal: "", progress: "", date_established: "", target_date: "", date_achieved: "", frequency: "", modality: "" };
}

export function emptyPresentingProblems(): PrpPresentingProblemsContent {
  return {
    diagnosis: "",
    diagnosis_duration: "",
    deficit_areas: [],
    treatment_expectations: "",
    strengths: "",
    weaknesses: "",
    additional_info: "",
    client_needs: "",
    client_abilities: "",
    client_preferences: "",
    long_term_goals: [emptyGoal()],
    short_term_goals: [emptyGoal()],
    discharge_plan: "",
    discharge_date: "",
    discharge_supports: [],
    psychiatrist_user_id: "",
    psychiatrist_name: "",
    clinician_user_id: "",
    clinician_name: "",
  };
}

export function parsePresentingProblems(raw?: string): PrpPresentingProblemsContent {
  const c = parseOnto(raw, emptyPresentingProblems());
  return {
    ...c,
    deficit_areas: c.deficit_areas ?? [],
    long_term_goals: c.long_term_goals?.length ? c.long_term_goals : [emptyGoal()],
    short_term_goals: c.short_term_goals?.length ? c.short_term_goals : [emptyGoal()],
    discharge_supports: c.discharge_supports ?? [],
  };
}

/** Referral impairment → presenting-problems deficit area. */
const IMPAIRMENT_TO_DEFICIT: Record<string, string> = {
  daily_living: "independent_living",
  self_care: "self_care",
  financial_support: "financial_management",
  support_system: "social_skills",
  employment: "vocational",
};

/** Start a Presenting Problems form from the client's accepted referral. */
export function presentingProblemsFromReferral(ref: PrpReferralContent | null, today: string): PrpPresentingProblemsContent {
  const c = emptyPresentingProblems();
  c.long_term_goals = [{ ...emptyGoal(), date_established: today }];
  c.short_term_goals = [{ ...emptyGoal(), date_established: today }];
  if (!ref) return c;
  c.diagnosis = ref.diagnoses
    .filter((d) => d.code.trim())
    .sort((a, b) => Number(b.primary) - Number(a.primary))
    .map((d) => `${d.code} ${d.description}`.trim())
    .join("; ");
  c.deficit_areas = [...new Set(ref.impairments.map((k) => IMPAIRMENT_TO_DEFICIT[k]).filter((k): k is string => !!k))];
  c.client_needs = ref.reasons_for_referral;
  return c;
}

const blank = (s?: string) => !s || s.trim() === "";
const isDate = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

export function presentingProblemsProblems(c: PrpPresentingProblemsContent): string[] {
  const p: string[] = [];
  if (blank(c.diagnosis)) p.push("Diagnosis");
  if (!c.deficit_areas.length) p.push("At least one functional deficit area");
  if (blank(c.treatment_expectations)) p.push("Individual treatment expectations");
  if (blank(c.strengths)) p.push("Strengths");
  if (blank(c.client_needs)) p.push("Client needs");
  const ltg = c.long_term_goals.filter((g) => !blank(g.goal));
  if (!ltg.length) p.push("At least one long-term goal");
  ltg.forEach((g, i) => !isDate(g.target_date) && p.push(`Long-term goal ${i + 1}: target date`));
  const stg = c.short_term_goals.filter((g) => !blank(g.goal));
  if (!stg.length) p.push("At least one short-term goal");
  stg.forEach((g, i) => {
    if (!isDate(g.target_date)) p.push(`Short-term goal ${i + 1}: target date`);
    if (blank(g.frequency) || blank(g.modality)) p.push(`Short-term goal ${i + 1}: frequency and treatment modality`);
  });
  if (blank(c.clinician_user_id)) p.push("Clinician who will sign (pick from the list)");
  if (c.psychiatrist_external) {
    if (blank(c.psychiatrist_name)) p.push("Outside psychiatrist's name");
    if (!/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]{2,}$/.test((c.psychiatrist_email ?? "").trim())) {
      p.push("Outside psychiatrist's email (for the signing link)");
    }
  } else if (blank(c.psychiatrist_user_id)) {
    p.push("Psychiatrist who will sign (pick from the list)");
  }
  return p;
}
